import React from "react"
import appInsightsLogger from "./features/logging/appInsightsLogger"

export function lazyLoad<T extends React.ComponentType<any>>(
  importFunc: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  let reloadAttempted = false

  // Wrap the import function to handle chunk-loading errors
  const wrappedImport = () =>
    importFunc().catch((error: Error) => {
      const isChunkLoadError = (error: Error): boolean => {
        return (
          error.name === "TypeError" && // General network error type
          /\/.+\.js$/.test(error.message) && // Match any URL ending with `.js`
          error.message.includes("http") // Ensure it's a network-related error
        )
      }

      if (isChunkLoadError(error)) {
        appInsightsLogger.traceWarning(
          "LazyLoad -- New updates to the application are available. Reload is required to continue.",
        )

        if (!reloadAttempted) {
          reloadAttempted = true

          // Clear caches and reload
          return caches
            .keys()
            .then((cacheNames) => {
              return Promise.all(
                cacheNames.map((cacheName) => caches.delete(cacheName)),
              )
            })
            .then(() => {
              window.location.reload()
              return new Promise<never>(() => {}) // Return a never-resolving promise after reload
            })
            .catch((cacheError) => {
              appInsightsLogger.traceError(
                "LazyLoad -- Failed to clear cache or reload index.html:",
                cacheError,
              )
              throw error // Propagate the original error if reload fails
            })
        }
      }

      throw error // Rethrow error if it's not a chunk-loading issue or reload has been attempted
    })

  // Use React.lazy to create the lazy-loaded component
  return React.lazy(wrappedImport)
}
